'use client'
import {useRef} from 'react'
import {tv} from 'tailwind-variants'
import {grid, gridItem} from '~/design-system/foundations'
import {HgHeading} from '~/design-system/hg/components'
import {cn} from '~/design-system/utils'
import type {HgHeroCinematicProps} from '../CinematicVariant/types'
import CinematicVariant from '../CinematicVariant'
import {Disclaimer} from '../Disclaimer'
import type {HgHeroBaseProps} from '../../types'
import {getHeadingContentTypeProps} from '../../utils'

export type HgCinematicHeroProps = HgHeroBaseProps & HgHeroCinematicProps

const cinematicHeroVariants = tv({
  slots: {
    gridSlot:
      'z-[3] col-start-1 col-end-auto row-start-1 row-end-auto mx-auto pt-navbar-height',
    headingContainer: 'lg:col-span-10 lg:col-start-4',
    heading: 'z-[1] text-text-default arcadia-display-6',
  },
})

export default function CinematicHero(props: HgCinematicHeroProps) {
  const {headline, disclaimer, subhead, classNames, breadcrumbs, alignment} = props
  const headingContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const {heading, headingContainer, gridSlot} = cinematicHeroVariants()

  return (
    <div
      className={cn('group grid h-fit w-full', classNames?.container)}
      ref={containerRef}
      onPointerMove={event => {
        updateHoverEffectCoordinates(event, containerRef.current)
      }}
    >
      <CinematicVariant {...props} />
      <div
        className={grid({
          className: gridSlot(),
        })}
      >
        <div
          className={gridItem({
            size: 'heading',
            className: headingContainer(),
          })}
          ref={headingContainerRef}
        >
          <HgHeading
            {...getHeadingContentTypeProps({
              props,
              buttonVariant: 'frosted',
            })}
            variation={'hero'}
            topSpacing={'s8'}
            breadcrumbs={breadcrumbs}
            className={heading()}
            headline={headline}
            subhead={subhead}
            alignment={alignment || 'center'}
          />
        </div>
      </div>
      <Disclaimer disclaimer={disclaimer} />
    </div>
  )
}

// Updates CSS custom properties for overlay hover effect positioning
function updateHoverEffectCoordinates(
  event: React.PointerEvent,
  container: HTMLDivElement | null
) {
  if (!container) return

  const rect = container.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  container.style.setProperty('--x', `${x}px`)
  container.style.setProperty('--y', `${y}px`)
}
